import { useBudgetEditor } from "@/hooks/use-budget-editor";
import { BudgetContent } from "./BudgetContent";
import { getMonths } from "@/utils/time";
import { useToastContext } from "@/context/ToastContext";
import { useEffect } from "react";
import { BudgetRow } from "./types";

interface BudgetUpdateHandlerProps {
  clientId: string;
  connectionId: string;
  budgetId: string;
  budgetName: string;
  isActive: boolean;
}

export const BudgetUpdateHandler = ({
  clientId,
  connectionId,
  budgetId,
  budgetName,
  isActive,
}: BudgetUpdateHandlerProps) => {
  const { showToast } = useToastContext();
  const {
    budgetData,
    isLoading,
    isError,
    rows,
    setRows,
    changedFields,
    setChangedFields,
    originalRows,
    setOriginalRows,
    updateExistingBudget,
    isUpdating,
    updateSuccess,
    updateError,
  } = useBudgetEditor(true, clientId, connectionId, budgetId);

  useEffect(() => {
    if (updateSuccess) {
      showToast({
        title: "Success",
        description: "Budget updated successfully",
        variant: "success",
      });
    }
  }, [updateSuccess, showToast]);

  useEffect(() => {
    if (updateError) {
      showToast({
        title: "Error",
        description: "Failed to update budget",
        variant: "error",
      });
    }
  }, [updateError, showToast]);

  const handleDiscard = () => {
    setRows(originalRows);
    setChangedFields(new Set());
  };

  const updateGroupTotals = (updatedRows: typeof rows, groupKey: string, month: string) => {
    const groupTotal = updatedRows
      .filter(row => row.groupKey === groupKey && !row.isGroupTotal)
      .reduce((sum, row) => sum + (row.amounts[month] || 0), 0);
    return updatedRows.map(row => {
      if (row.isGroupTotal && row.groupKey === groupKey) {
        return {
          ...row,
          amounts: {
            ...row.amounts,
            [month]: groupTotal
          }
        };
      }
      return row;
    });
  };

  const handleAmountChange = (rowId: string, month: string, value: string) => {
    const trimmedValue = value.replace(/\s/g, '');
    setRows(currentRows => {
      let updatedRows = currentRows.map(row => {
        if (row.id === rowId) {
          return {
            ...row,
            amounts: {
              ...row.amounts,
              [month]: parseFloat(trimmedValue) || 0
            }
          };
        }
        return row;
      });

      const changedRow = updatedRows.find(row => row.id === rowId);
      if (changedRow?.groupKey) {
        updatedRows = updateGroupTotals(updatedRows, changedRow.groupKey, month);
      }

      return updatedRows;
    });
    setChangedFields(prev => new Set(prev).add(`${rowId}-${month}`));
  };

  const handleAddRow = (accountName: string, accountNumber: number) => {
    // check if account number already exists
    if (rows.some(row => row.id === accountNumber.toString())) {
      showToast({
        title: "Error",
        description: "Account number already exists",
        variant: "error",
      });
      return;
    }

    // find first row which is not a group total
    const randomAccountIndex = rows.findIndex(row => !row.isGroupTotal);
    // we use this to copy the amounts from
    const amounts = { ...rows[randomAccountIndex].amounts };
    // set amounts to zero
    Object.keys(amounts).forEach(key => amounts[key] = 0);
    // handle that 5 & 6 is in same group
    let groupKey = accountNumber.toString()[0];
    if (groupKey === '5' || groupKey === '6') {
      groupKey = '5,6';
    }
    const newRow: BudgetRow = {
      id: accountNumber.toString(),
      number: accountNumber.toString(),
      name: accountName,
      description: accountName,
      groupKey: groupKey,
      isGroupTotal: false,
      amounts,
    };
    // find index of first row with higher id than new account number
    let insertIndex = rows.findIndex(row => !row.isGroupTotal && parseInt(row.id) > accountNumber);
    // make sure we are in the correct group still
    if (rows[insertIndex].groupKey > newRow.groupKey) {
      insertIndex--;
    }

    // insert new row at that index
    setRows([
      ...rows.slice(0, insertIndex),
      newRow,
      ...rows.slice(insertIndex),
    ]);

    // set each month as changed
    Object.keys(amounts).forEach(key => {
      setChangedFields(prev => new Set(prev).add(`${newRow.id}-${key}`));
    });
  };

  const handleDeleteRow = (rowId: string) => {
    // row to delete
    const rowToDelete = rows.find(row => row.id === rowId);

    // set all months changed
    Object.keys(rowToDelete?.amounts || {}).forEach(key => {
      setChangedFields(prev => new Set(prev).add(`${rowToDelete.id}-${key}`));
    });

    // remove row
    setRows(rows.filter(row => row.id !== rowId));
  };

  const handleClearRow = (rowId: string) => {
    const rowToClear = rows.find(row => row.id === rowId);
    if (!rowToClear) return;

    Object.keys(rowToClear.amounts).forEach(key => {
      handleAmountChange(rowId, key, '0');
    });
  }

  const handleSave = async () => {
    if (!budgetData) return;

    try {
      const accounts = rows
        .filter(row => !row.isGroupTotal)
        .map(row => {
          const months = getMonths(
            budgetData.yearFrom,
            budgetData.monthFrom,
            budgetData.yearTo,
            budgetData.monthTo
          ).map(monthStr => {
            const [year, month] = monthStr.split('-').map(Number);
            return {
              year,
              month,
              target: row.amounts[monthStr] || 0,
              history: null
            };
          });

          return {
            number: row.id,
            description: row.description,
            months
          };
        });

      console.log('accounts in put:', accounts);
      const updatePayload = {
        id: budgetData.id,
        budget: {
          id: budgetData.id,
          clientId: budgetData.clientId,
          connectionIds: budgetData.connectionIds || [],
          name: budgetName,
          isActive: false,
          yearFrom: budgetData.yearFrom,
          monthFrom: budgetData.monthFrom,
          yearTo: budgetData.yearTo,
          monthTo: budgetData.monthTo,
          projectionPercentage: budgetData.projectionPercentage,
          accounts: [...accounts]
        }
      };

      updateExistingBudget(updatePayload);
      setChangedFields(new Set());
      setOriginalRows(rows);
    } catch (error) {
      console.error('Error saving budget:', error);
    }
  };

  const months = budgetData ? getMonths(
    budgetData.yearFrom,
    budgetData.monthFrom,
    budgetData.yearTo,
    budgetData.monthTo
  ) : [];

  return (
    <BudgetContent
      isLoading={isLoading}
      isError={isError}
      isSaving={isUpdating}
      rows={rows}
      months={months}
      changedFields={changedFields}
      onAmountChange={handleAmountChange}
      onAddRow={handleAddRow}
      onDeleteRow={handleDeleteRow}
      onClearRow={handleClearRow}
      hasChanges={changedFields.size > 0}
      onDiscard={handleDiscard}
      onSave={handleSave}
      budgetData={budgetData}
    />
  );
};
